import {
  Box,
  List,
  ListItemButton,
  ListItemButtonProps,
  ListItemText,
  ListProps
} from '@mui/material'
import type CatalogProduct from '../types/CatalogProduct'
import { useTranslation } from 'react-i18next'
import { CatalogProductType } from '../enums/CatalogProductType'
import { formatApperentPower, formatNominalCurrent, formatNominalVoltage } from '../utils/string'
import { isSpineMultiMeasurementCatalogProduct } from '../utils/catalogProduct'
import type { SpineMultimeasurementCatalogProduct } from '../types/CatalogProduct'
import { useCallback, useEffect, useState } from 'react'
import uploadService from '../services/uploadService'
import ImageWithPreview from './ImageWithPreview'

export interface CatalogProductDetailsListProps extends Omit<ListProps, 'children'> {
  catalogProduct: CatalogProduct
  showName?: boolean
  showManufacturer?: boolean
  showManufacturerProductCode?: boolean
  showCustomData?: boolean
  includeMissing?: boolean
}

export default function CatalogProductDetailsList({
  catalogProduct,
  showName = true,
  showManufacturer = true,
  showManufacturerProductCode = true,
  showCustomData = true,
  includeMissing = true,
  ...props
}: CatalogProductDetailsListProps) {
  const { t } = useTranslation('catalogProduct')
  const [catalogProductPhotoUrl, setCatalogProductPhotoUrl] = useState<string | null>(null)

  const loadCatalogProductPhoto = useCallback(async (photoId: string, signal: AbortSignal) => {
    try {
      const result = await uploadService.getUpload(photoId, { signal })
      setCatalogProductPhotoUrl(result.url)
    } catch {
      if (!signal.aborted) {
        setCatalogProductPhotoUrl(null)
      }
    }
  }, [])

  useEffect(() => {
    const abortController = new AbortController()

    if (!catalogProduct.photo?.id) {
      setCatalogProductPhotoUrl(null)
      return
    }

    loadCatalogProductPhoto(catalogProduct.photo.id, abortController.signal)

    return () => {
      abortController.abort()
    }
  }, [catalogProduct.photo?.id, loadCatalogProductPhoto])

  const formatOutputs = (product: SpineMultimeasurementCatalogProduct) => {
    // Group by nominal current and count.
    const groups: { [key: string]: number } = {}
    for (const output of Object.values(product.customData.outputs)) {
      const key = `${output.nominalCurrent} A`
      groups[key] = (groups[key] || 0) + 1
    }

    // Format the groups into a string.
    return Object.entries(groups)
      .map(([key, count]) => `${count} x ${key}`)
      .join(' + ')
  }

  return (
    <List {...props}>
      {showName && (includeMissing || catalogProduct?.name) && (
        <CatalogProductNameListItem key="name" catalogProduct={catalogProduct} />
      )}

      {showManufacturer && (includeMissing || catalogProduct?.manufacturer) && (
        <CatalogProductManufacturerListItem key="manufacturer" catalogProduct={catalogProduct} />
      )}

      {showManufacturerProductCode &&
        (catalogProduct?.manufacturerProductCode || includeMissing) && (
          <CatalogProductManufacturerProductCodeListItem
            key="manufacturerProductCode"
            catalogProduct={catalogProduct}
          />
        )}

      {catalogProduct.type === CatalogProductType.GENERATOR &&
        showCustomData &&
        (catalogProduct?.customData?.apparentPower !== undefined || includeMissing) && (
          <ListItemButton key="customData.apparentPower">
            <ListItemText
              primary={
                catalogProduct?.customData?.apparentPower !== undefined
                  ? formatApperentPower(catalogProduct.customData.apparentPower)
                  : '-'
              }
              secondary={t('generator:apparentPower')}
            />
          </ListItemButton>
        )}

      {catalogProduct.type === CatalogProductType.GENERATOR &&
        showCustomData &&
        (catalogProduct?.customData?.nominalVoltage !== undefined || includeMissing) && (
          <ListItemButton key="customData.nominalVoltage">
            <ListItemText
              primary={
                catalogProduct?.customData?.nominalVoltage !== undefined
                  ? formatNominalVoltage(catalogProduct.customData.nominalVoltage)
                  : '-'
              }
              secondary={t('generator:nominalVoltage')}
            />
          </ListItemButton>
        )}

      {catalogProduct.type === CatalogProductType.GENERATOR &&
        showCustomData &&
        (catalogProduct?.customData?.nominalCurrent !== undefined || includeMissing) && (
          <ListItemButton key="customData.nominalCurrent">
            <ListItemText
              primary={
                catalogProduct?.customData?.nominalCurrent !== undefined
                  ? formatNominalCurrent(catalogProduct.customData.nominalCurrent)
                  : '-'
              }
              secondary={t('generator:nominalCurrent')}
            />
          </ListItemButton>
        )}

      {catalogProduct.type === CatalogProductType.GENERATOR &&
        showCustomData &&
        (catalogProduct?.customData?.powerSource !== undefined || includeMissing) && (
          <ListItemButton key="customData.powerSource">
            <ListItemText
              primary={
                catalogProduct?.customData?.powerSource !== undefined
                  ? t(`generator:${catalogProduct.customData.powerSource}`)
                  : '-'
              }
              secondary={t('generator:powerSource')}
            />
          </ListItemButton>
        )}

      {(catalogProduct.type === CatalogProductType.ELECTRICAL_CABINET ||
        catalogProduct.type === CatalogProductType.SPINE_MODULE) &&
        catalogProduct?.customData?.fuse !== undefined && (
          <ListItemButton key="customData.fuse">
            <ListItemText
              primary={
                catalogProduct?.customData?.fuse !== undefined
                  ? `${catalogProduct.customData.fuse} A`
                  : '-'
              }
              secondary={t('nominalCurrent')}
            />
          </ListItemButton>
        )}

      {(catalogProduct.type === CatalogProductType.ELECTRICAL_CABINET ||
        catalogProduct.type === CatalogProductType.SPINE_MODULE) &&
        catalogProduct?.customData?.inputCables !== undefined && (
          <ListItemButton key="customData.inputCables">
            <ListItemText
              primary={
                catalogProduct?.customData?.inputCables !== undefined
                  ? catalogProduct.customData.inputCables
                  : '-'
              }
              secondary={t('inputCables')}
            />
          </ListItemButton>
        )}

      {(catalogProduct.type === CatalogProductType.ELECTRICAL_CABINET ||
        catalogProduct.type === CatalogProductType.SPINE_MODULE) &&
        catalogProduct?.customData?.isMainDistributionAssembly !== undefined && (
          <ListItemButton key="customData.isMainDistributionAssembly">
            <ListItemText
              primary={
                catalogProduct?.customData?.isMainDistributionAssembly !== undefined
                  ? catalogProduct.customData.isMainDistributionAssembly
                    ? t('actions:yes')
                    : t('actions:nope')
                  : '-'
              }
              secondary={t('mainDistributionAssembly')}
            />
          </ListItemButton>
        )}

      {catalogProduct.type === CatalogProductType.HEATER &&
        catalogProduct.customData.heatingPower !== undefined && (
          <ListItemButton key="customData.heatingPower">
            <ListItemText
              primary={
                catalogProduct?.customData?.heatingPower !== undefined
                  ? `${catalogProduct.customData.heatingPower} kW`
                  : '-'
              }
              secondary={t('heater:heatingPower')}
            />
          </ListItemButton>
        )}

      {catalogProduct.type === CatalogProductType.AURORA &&
        catalogProduct.customData.heatingPower !== undefined && (
          <ListItemButton key="customData.heatingPower">
            <ListItemText
              primary={
                catalogProduct?.customData?.heatingPower !== undefined
                  ? `${catalogProduct.customData.heatingPower} kW`
                  : '-'
              }
              secondary={t('aurora:heatingPower')}
            />
          </ListItemButton>
        )}

      {catalogProduct.description !== null && catalogProduct.description !== '' && (
        <ListItemButton key="description">
          <ListItemText primary={catalogProduct.description} secondary={t('description')} />
        </ListItemButton>
      )}

      {isSpineMultiMeasurementCatalogProduct(catalogProduct) && (
        <>
          <ListItemButton key="outputs">
            <ListItemText primary={formatOutputs(catalogProduct)} secondary={t('outputs')} />
          </ListItemButton>

          {catalogProduct.customData?.shipAsInstallationKit !== undefined && (
            <ListItemButton key="shipAsInstallationKit">
              <ListItemText
                primary={
                  catalogProduct.customData?.shipAsInstallationKit
                    ? t('actions:yes')
                    : t('actions:nope')
                }
                secondary={t('shipAsInstallationKit')}
              />
            </ListItemButton>
          )}
        </>
      )}

      {catalogProductPhotoUrl ? (
        <ListItemButton key="photo" sx={{ display: 'block', alignItems: 'flex-start' }}>
          <ListItemText secondary={t('common:photo')} />
          <Box
            sx={{
              display: 'inline-block',
              overflow: 'hidden',
              borderRadius: 1,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              mt: 1
            }}
          >
            <ImageWithPreview
              src={catalogProductPhotoUrl}
              alt={catalogProduct.name}
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '280px',
                objectFit: 'contain'
              }}
            />
          </Box>
        </ListItemButton>
      ) : (
        <ListItemButton key="photo" disabled>
          <ListItemText primary={t('common:none')} secondary={t('common:photo')} />
        </ListItemButton>
      )}
    </List>
  )
}

export interface CatalogProductDetailsListItemProps extends Omit<ListItemButtonProps, 'children'> {
  catalogProduct: CatalogProduct
}

export function CatalogProductNameListItem({
  catalogProduct,
  ...props
}: CatalogProductDetailsListItemProps) {
  const { t } = useTranslation('catalogProduct')

  return (
    <ListItemButton {...props}>
      <ListItemText
        primary={catalogProduct.name ?? t('noName')}
        primaryTypographyProps={{
          color: catalogProduct.name ? 'inherit' : 'text.disabled'
        }}
        secondary={t('name')}
      />
    </ListItemButton>
  )
}

export function CatalogProductManufacturerListItem({
  catalogProduct,
  ...props
}: CatalogProductDetailsListItemProps) {
  const { t } = useTranslation('catalogProduct')

  return (
    <ListItemButton {...props}>
      <ListItemText
        primary={catalogProduct.manufacturer ?? t('noManufacturer')}
        primaryTypographyProps={{
          color: catalogProduct.manufacturer ? 'inherit' : 'text.disabled'
        }}
        secondary={t('manufacturer')}
      />
    </ListItemButton>
  )
}

export function CatalogProductManufacturerProductCodeListItem({
  catalogProduct,
  ...props
}: CatalogProductDetailsListItemProps) {
  const { t } = useTranslation('catalogProduct')

  return (
    <ListItemButton {...props}>
      <ListItemText
        primary={catalogProduct.manufacturerProductCode ?? t('noManufacturerProductCode')}
        primaryTypographyProps={{
          color: catalogProduct.manufacturerProductCode ? 'inherit' : 'text.disabled'
        }}
        secondary={t('manufacturerProductCode')}
      />
    </ListItemButton>
  )
}
